import { PersonaData, useChatStore } from "../stores/ChatStore";

import { useHistory } from "react-router-dom";
import { ChatConfig, ResponseMode } from "src/reusable/Chat/types";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { ImageContent } from "../types";

export const useAssistantNavigation = () => {
  const history = useHistory();
  const { basePath } = useChatStore((state) => state.productConfig);
  const {
    setConfig,
    setPersona,
    setHasTalkedFirst,
    setInitialUserMessage,
    setInitialSendId,
    setInitialImages,
    addUploadedFile,
    setSourceDataRecordIds,
    setComposerHandoff,
    config,
    resetDialogue,
    resetResponseMode,
    restoreResponseMode,
    lockResponseMode,
  } = useChatStore();

  const navigateToChat = ({
    dialogueId,
    options,
  }: {
    dialogueId: number;
    options?: {
      persona?: string;
      config?: ChatConfig;
      userMessage?: string;
      /** send_id minted when ai_message_sent was captured for userMessage,
       *  so the eventual ai_response_completed pairs with it (#29704) */
      sendId?: string;
      uploadedFiles?: FileUploadState[];
      /** VLM images to include with the initial message */
      images?: ImageContent[];
      /** Source data record IDs for document-scoped search ("Chat with Document") */
      sourceDataRecordIds?: number[];
      /** Response mode the caller already fixed for this conversation (splash submit). */
      responseMode?: ResponseMode;
    };
  }) => {
    // Reset dialogue state when navigating to a new chat
    // This ensures no old messages or artifacts persist
    resetDialogue();

    // Every entry into a new chat passes here, so the previous conversation's
    // mode (and its lock) must not leak into this one: start at the default,
    // unless the caller chose a mode for it. A navigation that carries a first
    // message (the empty FTUX / talk-first trigger included) starts the
    // conversation on arrival, so the mode is locked before the reply lands.
    if (options?.responseMode) {
      restoreResponseMode(options.responseMode);
    } else {
      resetResponseMode();
      if (options?.userMessage !== undefined) {
        lockResponseMode();
      }
    }

    // For chat pages, don't include persona in URL - config comes from dialogue data
    const sanitizedBasePath = basePath.endsWith("/")
      ? basePath.slice(0, -1)
      : basePath;
    const path = `${sanitizedBasePath}/chat/${dialogueId}`;

    // Apply the complete config from options (splash screen state with user modifications)
    if (options?.config) {
      console.log(
        "📋 [useAssistantNavigation] Applying config from navigation",
        {
          dialogueId,
          modelId: options.config.modelId,
          datasetCount: options.config.datasets?.length || 0,
          chatConfigId: options.config.chatConfigId,
          timestamp: new Date().toISOString(),
        },
      );

      // Use the config as-is — it carries operational fields
      setConfig(options.config);

      // Persona identity is preserved from the store (set by useSyncPersonaToStore).
      // If config carries legacy identity fields, extract persona from them as fallback.
      const existingPersona = useChatStore.getState().persona;
      if (
        !existingPersona &&
        options.config.name &&
        options.config.slug &&
        options.config.chatConfigId
      ) {
        const personaData: PersonaData = {
          id: options.config.chatConfigId,
          slug: options.config.slug,
          name: options.config.name,
          description: options.config.description || "",
          initialPrompt: options.config.initialPrompt || "",
          systemPrompt: options.config.systemPrompt || "",
          datasets: options.config.datasets || [],
        };
        setPersona(personaData);
      } else if (
        !existingPersona &&
        options.config.datasets &&
        options.config.datasets.length > 0
      ) {
        // No persona but has datasets - clear persona but keep config
        setPersona(null);
      }
    }

    // The composer's pick is newer than what the dialogue saved at creation,
    // so useLoadDialogue keeps it until the first turn. Only a navigation that
    // hands over a config is a handoff; any other (FTUX) adopts its record.
    setComposerHandoff(
      options?.config
        ? {
            dialogueId,
            config: options.config,
            persona: useChatStore.getState().persona,
          }
        : null,
    );

    // Handle uploaded files (project files or other attachments)
    if (options?.uploadedFiles && options.uploadedFiles.length > 0) {
      // Get current files from store
      const currentFiles = useChatStore.getState().uploadedFiles;

      // Check if we're adding project files (files marked with isFromProject)
      const hasProjectFiles = options.uploadedFiles.some(
        (f: any) => f.isFromProject === true,
      );

      if (hasProjectFiles) {
        // When navigating from a project, clear ALL existing files
        // This ensures we start fresh with only the new project's files
        useChatStore.getState().clearUploadedFiles();
      } else {
        // For non-project navigation, only clear existing project files
        const projectFilesToRemove = currentFiles.filter(
          (f: any) => f.isFromProject === true,
        );

        if (projectFilesToRemove.length > 0) {
          projectFilesToRemove.forEach((file) => {
            useChatStore.getState().removeUploadedFile(file.id);
          });
        }
      }

      // Add each file (the store will do its own deduplication check as a safety net)
      options.uploadedFiles.forEach((file) => addUploadedFile(file));
    }

    // Handle source data record IDs for document-scoped search ("Chat with Document")
    if (
      options?.sourceDataRecordIds &&
      options.sourceDataRecordIds.length > 0
    ) {
      console.log(
        "📄 [useAssistantNavigation] Setting source data record IDs",
        {
          dialogueId,
          sdrIds: options.sourceDataRecordIds,
          timestamp: new Date().toISOString(),
        },
      );
      setSourceDataRecordIds(options.sourceDataRecordIds);
    }

    // Always set the user message when navigating (clear it if not provided)
    // This ensures old messages don't persist and cause auto-submission
    setInitialUserMessage(options?.userMessage ?? undefined);
    setInitialSendId(options?.sendId ?? undefined);

    // Set initial images for VLM support (from splash screen)
    setInitialImages(options?.images || undefined);

    // Only set hasTalkedFirst if we have an actual user message
    // This prevents auto-submission when just opening a new chat
    if (options?.userMessage) {
      setHasTalkedFirst(true);
    } else {
      setHasTalkedFirst(false);
    }

    // Small delay to ensure state is persisted to sessionStorage before navigation
    setTimeout(() => {
      history.replace(path);
    }, 50);

    // Reset navigation flag after navigation completes
    // setTimeout(() => setNavigatingToChat(false), 100);
  };

  return {
    navigateToChat,
    // Return current config from store
    initialConfig: config,
    // initialUserMessage removed - can be added back to ChatStore if needed
  };
};

export default useAssistantNavigation;
